import { createFileRoute, notFound } from "@tanstack/react-router"

import Link from "@/components/link"
import { PageNotFound } from "@/components/feedback/page-not-found"
import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { HelpArticleError } from "@/components/help/help-article-error"
import { HelpArticleLoading } from "@/components/help/help-article-loading"
import { ArticleBody } from "@/components/help/article-body"
import { Container } from "@/components/layout/container"
import { HelpFrame, sectionId } from "@/components/help/help-frame"
import { Breadcrumb } from "@/components/layout/breadcrumb"
import { StillStuck } from "@/components/help/still-stuck"
import { SiteShell } from "@/components/layout/site-shell"
import { JsonLd, absoluteUrl, organizationRef } from "@/components/seo/json-ld"
import { Button } from "@anysola/ui/button"
import { ACCOUNT_URL } from "@/lib/app-links"
import { pageHead } from "@/lib/metadata"
import { getHelpArticle } from "@/server/web"

/**
 * One help article, cached like /news/$slug: read by the service and kept
 * 300 s; saving an article in Admin revalidates its path at once.
 */
export const Route = createFileRoute("/help/$slug")({
  loader: async ({ params }) => {
    const read = await getHelpArticle({ data: params.slug })
    if (read && !read.article) throw notFound()
    return { read }
  },
  head: ({ loaderData, params }) => {
    // A missing article (the loader threw notFound): Next's generateMetadata
    // still named it, with the 404 page under it.
    if (!loaderData) {
      return pageHead({
        title: "Article not found",
        description: "This help article doesn’t exist or was removed.",
        path: `/help/${params.slug}`,
        index: false,
      })
    }
    const article = loaderData.read?.article
    if (!article) {
      // An unreadable database is not a missing article: no "not found" title for it.
      return pageHead({
        title: "Help center",
        description:
          "Guides for your Anysola account, billing and support tickets.",
        path: `/help/${params.slug}`,
        index: false,
      })
    }
    return pageHead({
      title: article.title,
      description: article.summary,
      path: `/help/${article.slug}`,
      image: `/help/${article.slug}/opengraph-image`,
    })
  },
  component: HelpArticlePage,
  pendingComponent: HelpArticleLoading,
  errorComponent: HelpArticleError,
  notFoundComponent: PageNotFound,
})

function HelpArticlePage() {
  const { read } = Route.useLoaderData()
  if (!read) {
    return (
      <SiteShell>
        <h1 className="sr-only">Help center</h1>
        <Container className="pt-10 pb-14 sm:pt-12 sm:pb-16">
          <UnavailableAlert title="This article is unavailable right now." />
        </Container>
      </SiteShell>
    )
  }
  const article = read.article!

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: article.title,
    description: article.summary,
    datePublished: article.createdAt.toISOString(),
    dateModified: article.updatedAt.toISOString(),
    mainEntityOfPage: absoluteUrl(`/help/${article.slug}`),
    image: absoluteUrl(`/help/${article.slug}/opengraph-image`),
    ...(article.section ? { articleSection: article.section } : {}),
    author: organizationRef(),
    publisher: organizationRef(),
  }

  return (
    <SiteShell>
      <JsonLd data={jsonLd} />
      <HelpFrame current={article.section ?? undefined}>
        <div className="max-w-[42rem] space-y-8">
          <header>
            <Breadcrumb
              items={[
                { label: "Help", href: "/help" },
                ...(article.section
                  ? [
                      {
                        label: article.section,
                        href: `/help#${sectionId(article.section)}`,
                      },
                    ]
                  : []),
              ]}
            />
            <h1 className="mt-4 text-3xl leading-tight font-semibold tracking-[-0.035em] text-balance text-foreground sm:text-4xl">
              {article.title}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              {article.summary}
            </p>
          </header>

          <ArticleBody body={article.body} />

          <StillStuck>
            <Button
              variant="secondary"
              render={<Link href={`${ACCOUNT_URL}/support?new=1`} />}
            >
              Contact support
            </Button>
          </StillStuck>
        </div>
      </HelpFrame>
    </SiteShell>
  )
}
